
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { Button } from '@/components/ui/Button';
import { MarketplaceItem, UserRole } from '@/types';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { MoreVertical, Edit, Trash2, Download, Loader2 } from 'lucide-react';
import { useTableState } from '@/hooks/useTableState';
import { DataTablePagination } from '@/components/tables/DataTablePagination';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import BuyTicketButton from '@/components/ui/BuyTicketButton';

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
    <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
            status === 'Available'
                ? 'bg-green-500/20 text-green-300'
                : status === 'Sold'
                ? 'bg-yellow-500/20 text-yellow-300'
                : 'bg-red-500/20 text-red-300'
        }`}
    >
        {status}
    </span>
);

const MarketplaceRealDataTable = ({ currentUserId, currentUserRole }: { currentUserId: string, currentUserRole: UserRole }) => {
  const { notify } = useToast();
  const [marketItems, setMarketItems] = useState<MarketplaceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleTicketPurchase = (eventId: string, userId: string, quantity: number) => {
    notify(`Ticket purchase for event ${eventId} for user ${userId} with quantity ${quantity} is being processed.`, 'info');
  };

  useEffect(() => {
    const fetchItems = async () => {
      setIsLoading(true);
      try {
        // Corrected API endpoint
        const response = await fetch(`${API_BASE_URL}/api/marketplace`);
        if (!response.ok) {
          throw new Error('Failed to fetch from API');
        }
        const data: MarketplaceItem[] = await response.json();
        if (data && data.length > 0) {
          setMarketItems(data);
        } else {
          throw new Error('API returned no data, using fallback.');
        }
      } catch (error) {
        console.warn('Live data fetch failed:', error);
        notify('Could not load live marketplace data. Using mock data instead.', 'warn');
        try {
          const fallbackResponse = await fetch('/marketplace-items.json');
          const fallbackData: MarketplaceItem[] = await fallbackResponse.json();
          setMarketItems(fallbackData);
        } catch (fallbackError) {
          console.error("Failed to load fallback mock data", fallbackError);
          notify('Critical: Could not load any marketplace data.', 'error');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchItems();
  }, [notify]);

  const {
    pageIndex,
    pageSize,
    setPageIndex,
    setPageSize,
    getPaginatedData,
  } = useTableState<MarketplaceItem>(marketItems, 10);

  const handleDelete = (itemId: string) => {
    setMarketItems(currentItems => currentItems.filter(item => item.id !== itemId));
    notify(`Item ${itemId} has been successfully deleted.`, 'success');
  };

  const handleDownload = () => {
    const isAdmin = currentUserRole === UserRole.ADMIN || currentUserRole === UserRole.SYSTEM_ADMIN_LIVE;
    const dataToDownload = isAdmin ? marketItems : marketItems.filter(item => item.sellerId === currentUserId);
    
    if (dataToDownload.length === 0) {
      notify("You have no data to download.", "info");
      return;
    }

    const jsonString = JSON.stringify(dataToDownload, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = isAdmin ? 'all-marketplace-data.json' : 'my-marketplace-data.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notify("Data download started.", "success");
  };

  const paginatedData = getPaginatedData();
  const pageCount = Math.ceil(marketItems.length / pageSize);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 bg-kala-900 text-white rounded-lg">
        <Loader2 className="animate-spin h-8 w-8 mr-3" />
        Loading Marketplace Data...
      </div>
    );
  }

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Marketplace Items</h2>
        <Button onClick={handleDownload} className="bg-kala-secondary text-kala-900 px-4 py-2 rounded-lg font-bold hover:bg-cyan-400 transition-colors flex items-center gap-2">
          <Download className="w-4 h-4" />
          Download Data
        </Button>
      </div>
      <Table>
        <TableCaption>A list of items available on the KalaKrut marketplace. {pageCount > 1 && `(Page ${pageIndex + 1} of ${pageCount})`}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead>Seller</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedData.map((item) => {
            const canManage = currentUserRole === UserRole.ADMIN || currentUserRole === UserRole.SYSTEM_ADMIN_LIVE || item.sellerId === currentUserId;
            
            let eventId: string | null = null;
            if (item.type === 'Ticket' && item.description) {
                const match = item.description.match(/EVENT_ID::(.*?);;/);
                if (match && match[1]) {
                    eventId = match[1];
                }
            }

            return (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.title}</TableCell>
                <TableCell>{item.seller?.name || 'Unknown Seller'}</TableCell>
                <TableCell>{item.price} {item.currency}</TableCell>
                <TableCell><StatusPill status={item.status} /></TableCell>
                <TableCell>
                    <div className="flex items-center gap-2">
                        {eventId && <BuyTicketButton eventId={eventId} currentUserId={currentUserId} onTicketPurchase={handleTicketPurchase} quantity={1} />}

                        {canManage && !eventId && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                        <MoreVertical className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => notify(`Edit action for ${item.id} not implemented yet.`, 'info')}>
                                        <Edit className="mr-2 h-4 w-4" />
                                        <span>Edit</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="text-red-400" onClick={() => handleDelete(item.id)}>
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        <span>Delete</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <DataTablePagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        pageSize={pageSize}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
        />
    </div>
  );
};

export default MarketplaceRealDataTable;
